import { FinalScoreValence } from '@upright/common/model/output/scores/drilldowns/types';
import { Leena2CategoryTopImpacts } from '@upright/common/model/output/scores/drilldowns/leena2-top-impacts-types';
import { fetchOrThrow } from '@upright/ui/api/common/fetchers/fetch-or-throw';
import { useQuery } from '@tanstack/react-query';

/**
 * Fetches the Leena-2-derived "Top impacts" leaf list for a single net-impact
 * category (impactCode + valence) of a company variant result. Mirrors
 * `useCompanyDrilldownContributions`.
 *
 * `enabled` is gated on the caller-supplied `enabled` flag (the team-only view
 * setting, see `useViewSetting('companyProduct.leena2TopImpactsInDrilldown')`)
 * plus all required params being present, so the request never fires for
 * non-team users or when the toggle is off. The endpoint also enforces
 * team-only server-side.
 */
export function useLeena2CategoryTopImpacts({
  resultId,
  impactCode,
  valence,
  enabled,
}: {
  resultId: string | undefined;
  impactCode: string;
  valence: FinalScoreValence;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: ['queryLeena2CategoryTopImpacts', resultId, impactCode, valence],

    queryFn: async () => {
      const search = new URLSearchParams({ impactCode, valence });
      return await fetchOrThrow<Leena2CategoryTopImpacts>(
        `/company/${String(resultId)}/leena2-category-top-impacts?${search.toString()}`,
      );
    },

    enabled: enabled && resultId !== undefined,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

/**
 * The same list for one product: its own Leena-2 topics behind a category, in the company list's
 * shape (the product is its only contributor). Team-only server-side too.
 */
export function useLeena2ProductImpactTopics({
  productCode,
  impactCode,
  valence,
  enabled,
}: {
  productCode: string | undefined;
  impactCode: string;
  valence: FinalScoreValence;
  enabled: boolean;
}) {
  return useQuery({
    queryKey: ['queryLeena2ProductImpactTopics', productCode, impactCode, valence],

    queryFn: async () => {
      const search = new URLSearchParams({ impactCode, valence });
      return await fetchOrThrow<Leena2CategoryTopImpacts>(
        `/leena2-harness/products/${encodeURIComponent(String(productCode))}/impact-topics?${search.toString()}`,
      );
    },

    enabled: enabled && productCode !== undefined,
    refetchOnWindowFocus: false,
    retry: false,
  });
}
